import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of rain streaks, default 40 */
  count?: number;
  /** Streak colour (rgba), default light blue */
  color?: string;
  /** Minimum fall duration seconds, default 0.5 */
  minDur?: number;
  /** Maximum fall duration seconds, default 1.1 */
  maxDur?: number;
  /** Tilt angle in degrees, default 12 */
  angle?: number;
}

/**
 * Rain — diagonal streaks falling quickly across the banner.
 */
const Rain: React.FC<Props> = ({
  count = 40,
  color = "rgba(190,220,255,0.5)",
  minDur = 0.5,
  maxDur = 1.1,
  angle = 12,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      len: 14 + r(1) * 18,
      dur: minDur + r(2) * (maxDur - minDur),
      delay: r(3) * 2,
    }),
    [count, minDur, maxDur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-rain {
          0%   { transform: translateY(-10vh) rotate(${angle}deg); opacity: 0; }
          8%   { opacity: 0.8; }
          100% { transform: translateY(110vh) rotate(${angle}deg); opacity: 0; }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: 1.4,
            height: p.len,
            background: `linear-gradient(to bottom, transparent, ${color})`,
            animation: `oot-rain ${p.dur}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
};

export default Rain;
